"use client";

import { useTranslator } from "~/i18n/provider";

import { formatMoney } from "~/lib/money";
import { cn } from "~/lib/utils";
import {
  type CustomOptionDef,
  CUSTOM_TEXT_HARD_MAX,
} from "~/server/api/schemas/personalisation";

import type { PersonalisationState } from "./use-personalisation";

/** "+£3.00" style delta suffix, or "" for a free choice. */
function deltaLabel(minor: number | undefined, currency: string): string {
  return minor && minor > 0 ? ` (+${formatMoney(minor, currency)})` : "";
}

function FieldWrapper({
  def,
  error,
  showError,
  hint,
  children,
}: {
  def: CustomOptionDef;
  error: string | undefined;
  showError: boolean;
  hint?: React.ReactNode;
  children: (ids: {
    errorId: string;
    hintId: string;
    describedBy?: string;
  }) => React.ReactNode;
}) {
  const errorId = `custom-${def.id}-error`;
  const hintId = `custom-${def.id}-hint`;
  const describedBy =
    [showError && error ? errorId : null, hint ? hintId : null]
      .filter(Boolean)
      .join(" ") || undefined;
  return (
    <div className="mt-5">
      {children({ errorId, hintId, describedBy })}
      {hint && (
        <p
          id={hintId}
          className="mt-1 text-xs text-fg-muted"
          aria-live="polite"
        >
          {hint}
        </p>
      )}
      {showError && error && (
        <p id={errorId} className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function RequiredMark({ required }: { required: boolean }) {
  return required ? (
    <span className="text-danger" aria-hidden>
      {" "}
      *
    </span>
  ) : null;
}

/**
 * P3-07 · Per-product custom fields (monogram text, dropdown add-ons,
 * checkboxes). Controlled by {@link PersonalisationState}: it renders inputs by
 * type with live char counters, per-option price deltas, and error messaging
 * associated via `aria-describedby`. State (values, validity, the price delta
 * shown in the buy box) lives in the hook so add-to-cart (P3-08) reads the same
 * payload. Renders nothing when the product defines no options.
 */
export function CustomOptions({
  defs,
  state,
  currency,
  className,
}: {
  defs: CustomOptionDef[];
  state: PersonalisationState;
  currency: string;
  className?: string;
}) {
  const t = useTranslator();
  if (defs.length === 0) return null;
  const { values, setValue, errors, touched, markTouched } = state;

  return (
    <div className={className} data-testid="pdp-custom-options">
      {defs.map((def) => {
        const error = errors[def.id];
        const showError = Boolean(touched[def.id] && error);
        const inputClass = cn(
          "mt-2 w-full rounded-[var(--radius-md)] border bg-surface px-3 py-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          showError ? "border-danger" : "border-border",
        );

        if (def.type === "TEXT" || def.type === "TEXTAREA") {
          const max = def.maxLength ?? CUSTOM_TEXT_HARD_MAX;
          const current =
            typeof values[def.id] === "string"
              ? (values[def.id] as string)
              : "";
          const counter = `${current.length}/${max}`;
          const priceNote = deltaLabel(def.priceDeltaMinor, currency);
          return (
            <FieldWrapper
              key={def.id}
              def={def}
              error={error}
              showError={showError}
              hint={
                <>
                  <span className="tabular-nums">{counter}</span>
                  {priceNote && <span>{` · adds${priceNote.slice(1)}`}</span>}
                </>
              }
            >
              {({ describedBy }) => (
                <>
                  <label
                    htmlFor={`custom-${def.id}`}
                    className="text-sm font-medium text-fg"
                  >
                    {def.label}
                    <RequiredMark required={def.required} />
                  </label>
                  {def.type === "TEXT" ? (
                    <input
                      id={`custom-${def.id}`}
                      type="text"
                      maxLength={max}
                      required={def.required}
                      value={current}
                      aria-invalid={showError || undefined}
                      aria-describedby={describedBy}
                      onChange={(e) => setValue(def.id, e.target.value)}
                      onBlur={() => markTouched(def.id)}
                      className={inputClass}
                    />
                  ) : (
                    <textarea
                      id={`custom-${def.id}`}
                      maxLength={max}
                      required={def.required}
                      rows={3}
                      value={current}
                      aria-invalid={showError || undefined}
                      aria-describedby={describedBy}
                      onChange={(e) => setValue(def.id, e.target.value)}
                      onBlur={() => markTouched(def.id)}
                      className={cn(inputClass, "resize-y")}
                    />
                  )}
                </>
              )}
            </FieldWrapper>
          );
        }

        if (def.type === "SELECT") {
          const current =
            typeof values[def.id] === "string"
              ? (values[def.id] as string)
              : "";
          return (
            <FieldWrapper
              key={def.id}
              def={def}
              error={error}
              showError={showError}
            >
              {({ describedBy }) => (
                <>
                  <label
                    htmlFor={`custom-${def.id}`}
                    className="text-sm font-medium text-fg"
                  >
                    {def.label}
                    <RequiredMark required={def.required} />
                  </label>
                  <select
                    id={`custom-${def.id}`}
                    required={def.required}
                    value={current}
                    aria-invalid={showError || undefined}
                    aria-describedby={describedBy}
                    onChange={(e) => {
                      setValue(def.id, e.target.value);
                      markTouched(def.id);
                    }}
                    className={inputClass}
                  >
                    <option value="">{t("pdp.selectPlaceholder")}</option>
                    {def.options?.map((opt) => (
                      <option key={opt.label} value={opt.label}>
                        {opt.label}
                        {deltaLabel(opt.priceDeltaMinor, currency)}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </FieldWrapper>
          );
        }

        // CHECKBOX
        const checked = values[def.id] === true;
        const priceNote = deltaLabel(def.priceDeltaMinor, currency);
        return (
          <FieldWrapper
            key={def.id}
            def={def}
            error={error}
            showError={showError}
          >
            {({ describedBy }) => (
              <label className="mt-2 flex items-center gap-2 text-sm text-fg">
                <input
                  id={`custom-${def.id}`}
                  type="checkbox"
                  checked={checked}
                  required={def.required}
                  aria-invalid={showError || undefined}
                  aria-describedby={describedBy}
                  onChange={(e) => {
                    setValue(def.id, e.target.checked);
                    markTouched(def.id);
                  }}
                  className="size-4 rounded border-border text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <span>
                  {def.label}
                  {priceNote}
                  <RequiredMark required={def.required} />
                </span>
              </label>
            )}
          </FieldWrapper>
        );
      })}
    </div>
  );
}
